import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Field, Input, Select } from "./ui";
import { ApiError } from "../lib/api";
import type { DomainContact, DomainContactInput } from "../lib/types";
import { useSaveDomainContact } from "../lib/queries";
import { intlLocaleFor } from "../i18n/locale";

/**
 * The registrant: who a domain is registered to.
 *
 * ICANN requires a real name and a postal address on every registration,
 * and the registrar refuses one without them — after the credit is taken,
 * which is why the form is complete here rather than lenient. One registrant
 * per organization: a company registering three names does not want to type
 * its address three times, and a registrant change is an owner change at the
 * registry, which is not something to do by accident per domain.
 */
const FIELDS = [
  "firstName", "lastName", "companyName", "email", "phoneCountryCode", "phoneNumber",
  "street", "houseNumber", "city", "state", "zipcode", "country",
] as const;

type FieldName = (typeof FIELDS)[number];

const OPTIONAL: ReadonlySet<FieldName> = new Set(["companyName", "state"]);

/* Two per row where the pair reads as one line of an address. */
const ROWS: FieldName[][] = [
  ["firstName", "lastName"],
  ["companyName"],
  ["email"],
  ["phoneCountryCode", "phoneNumber"],
  ["street", "houseNumber"],
  ["city", "state"],
  ["zipcode", "country"],
];

/**
 * Calling codes a customer of this platform is likely to be from. A free
 * text field behind them for everyone else: the registrar validates the
 * code, and a select of two hundred entries is a worse control than a box.
 *
 * Names come from `Intl.DisplayNames` in the reader's language rather than
 * from a column here, so "United States" reads as "Amerika Serikat" for an
 * Indonesian reader without a translation file carrying twenty countries.
 */
const COUNTRIES: { iso: string; code: string }[] = [
  { iso: "ID", code: "+62" },
  { iso: "SG", code: "+65" },
  { iso: "MY", code: "+60" },
  { iso: "PH", code: "+63" },
  { iso: "TH", code: "+66" },
  { iso: "VN", code: "+84" },
  { iso: "IN", code: "+91" },
  { iso: "AU", code: "+61" },
  { iso: "JP", code: "+81" },
  { iso: "KR", code: "+82" },
  { iso: "HK", code: "+852" },
  { iso: "TW", code: "+886" },
  { iso: "AE", code: "+971" },
  { iso: "GB", code: "+44" },
  { iso: "DE", code: "+49" },
  { iso: "FR", code: "+33" },
  { iso: "NL", code: "+31" },
  { iso: "US", code: "+1" },
  { iso: "CA", code: "+1" },
  { iso: "BR", code: "+55" },
];

/** The country's name in the reader's language, falling back to the code itself. */
function countryName(iso: string, locale: string | undefined): string {
  try {
    return new Intl.DisplayNames(intlLocaleFor(locale) ?? "en", { type: "region" }).of(iso) ?? iso;
  } catch {
    return iso;
  }
}

/** The registrar's refusal, or the API's, placed on the field it names. */
function issuesOf(error: unknown): Partial<Record<FieldName, string>> {
  if (!(error instanceof ApiError)) return {};
  const issues = (error.details as { issues?: { path?: (string | number)[]; message?: string }[] } | undefined)?.issues;
  if (!Array.isArray(issues)) return {};
  const out: Partial<Record<FieldName, string>> = {};
  for (const issue of issues) {
    const field = issue.path?.find((part): part is FieldName => typeof part === "string" && (FIELDS as readonly string[]).includes(part));
    if (field && issue.message && !out[field]) out[field] = issue.message;
  }
  return out;
}

function fromContact(contact: DomainContact | null): Record<FieldName, string> {
  return {
    firstName: contact?.firstName ?? "",
    lastName: contact?.lastName ?? "",
    companyName: contact?.companyName ?? "",
    email: contact?.email ?? "",
    phoneCountryCode: contact?.phoneCountryCode ?? "",
    phoneNumber: contact?.phoneNumber ?? "",
    street: contact?.street ?? "",
    houseNumber: contact?.houseNumber ?? "",
    city: contact?.city ?? "",
    state: contact?.state ?? "",
    zipcode: contact?.zipcode ?? "",
    country: contact?.country ?? "",
  };
}

export function DomainContactForm({
  orgId,
  contact,
  onSaved,
  onCancel,
}: {
  orgId: string;
  contact: DomainContact | null;
  onSaved: (contact: DomainContact) => void;
  onCancel?: () => void;
}) {
  const { t, i18n } = useTranslation("domains");
  const { t: tc } = useTranslation("common");
  const [draft, setDraft] = useState(fromContact(contact));
  const [attempted, setAttempted] = useState(false);
  const save = useSaveDomainContact(orgId);

  const missing = (field: FieldName) => attempted && !OPTIONAL.has(field) && draft[field].trim() === "";
  const serverIssues = issuesOf(save.error);
  const errorFor = (field: FieldName) => (missing(field) ? t("new.contact.needed") : (serverIssues[field] ?? null));
  // A refusal the form could place on a field is shown there and not twice.
  const bannerError = save.error && Object.keys(serverIssues).length === 0 ? save.error.message : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (FIELDS.some(missing)) return;
    const input: DomainContactInput = {
      ...draft,
      companyName: draft.companyName.trim() || undefined,
      state: draft.state.trim() || undefined,
    };
    save.mutate(input, { onSuccess: (result) => onSaved(result.data) });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {bannerError && <Alert>{bannerError}</Alert>}
      {ROWS.map((row) => (
        <div key={row.join("-")} className={row.length === 2 ? "grid gap-4 sm:grid-cols-2" : undefined}>
          {row.map((field) => (
            <Field
              key={field}
              label={t(`new.contact.fields.${field}`)}
              htmlFor={`contact-${field}`}
              error={errorFor(field)}
              hint={field === "phoneNumber" ? t("new.contact.phoneHint") : undefined}
            >
              {field === "phoneCountryCode" ? (
                <PickOrType
                  id="contact-phoneCountryCode"
                  value={draft.phoneCountryCode}
                  options={COUNTRIES.map((c) => ({ value: c.code, label: `${countryName(c.iso, i18n.language)} (${c.code})` }))}
                  placeholder="+62"
                  autoComplete="tel-country-code"
                  onChange={(value) => setDraft({ ...draft, phoneCountryCode: value })}
                />
              ) : field === "country" ? (
                <PickOrType
                  id="contact-country"
                  value={draft.country}
                  options={COUNTRIES.map((c) => ({ value: c.iso, label: `${countryName(c.iso, i18n.language)} (${c.iso})` }))}
                  placeholder="ID"
                  autoComplete="country"
                  maxLength={2}
                  onChange={(value) => setDraft({ ...draft, country: value.toUpperCase() })}
                />
              ) : (
                <Input
                  id={`contact-${field}`}
                  value={draft[field]}
                  onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
                  autoComplete={AUTOCOMPLETE[field]}
                  inputMode={field === "phoneNumber" ? "tel" : undefined}
                  placeholder={field === "phoneNumber" ? "0812 3456 7890" : undefined}
                />
              )}
            </Field>
          ))}
        </div>
      ))}
      <p className="text-xs leading-5 text-fg-subtle">{t("new.contact.verification")}</p>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" loading={save.isPending}>
          {t("new.contact.save")}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {tc("modal.cancel")}
          </Button>
        )}
      </div>
    </form>
  );
}

const AUTOCOMPLETE: Record<FieldName, string> = {
  firstName: "given-name",
  lastName: "family-name",
  companyName: "organization",
  email: "email",
  phoneCountryCode: "tel-country-code",
  phoneNumber: "tel-national",
  street: "address-line1",
  houseNumber: "address-line2",
  city: "address-level2",
  state: "address-level1",
  zipcode: "postal-code",
  country: "country",
};

/**
 * A select of the likely values, with "another" opening a box. One control
 * from the form's point of view: the value is always the string the API
 * gets. A select of two hundred countries is a worse control than a box for
 * everyone the list misses, and the registrar validates either way.
 */
function PickOrType({
  id,
  value,
  options,
  placeholder,
  autoComplete,
  maxLength,
  onChange,
}: {
  id: string;
  value: string;
  options: { value: string; label: string }[];
  placeholder: string;
  autoComplete: string;
  maxLength?: number;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation("domains");
  const known = options.some((option) => option.value === value);
  const [other, setOther] = useState(value !== "" && !known);
  if (other) {
    return (
      <div className="flex gap-2">
        <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete={autoComplete} maxLength={maxLength} />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setOther(false);
            onChange("");
          }}
        >
          {t("new.contact.pickCode")}
        </Button>
      </div>
    );
  }
  return (
    <Select
      id={id}
      value={known ? value : ""}
      onChange={(e) => {
        if (e.target.value === "__other") {
          setOther(true);
          onChange("");
        } else onChange(e.target.value);
      }}
    >
      <option value="">{t("new.contact.chooseCode")}</option>
      {options.map((option) => (
        <option key={option.value + option.label} value={option.value}>
          {option.label}
        </option>
      ))}
      <option value="__other">{t("new.contact.otherCode")}</option>
    </Select>
  );
}
